import type { ReactNode } from 'react';
import { useState, useCallback } from 'react';
import { Text, Stack } from '@hotjar/hotsauce';
import type { AbstractSingleFilterDropdownProps } from '../../SingleFilterType';
import { GenericFilterBody } from '../GenericFilter/GenericFilterBody';
import { GenericFilterDropdown } from '../GenericFilter/GenericFilterDropdown';
import { GenericFilterFooter } from '../GenericFilter/GenericFilterFooter';
import { FilterItemRadio, FilterRadioContainer } from '../GenericSubElement/FilterItemRadio';
import { GenericFilterHeader } from '../GenericFilter/GenericFilterHeader';

export interface Option<T> {
  value: T;
  label: string;
  content?: ReactNode;
}

enum ValidationErrorEnum {
  SELECT_ONE_OPTION = 'Select one option',
}

interface GenericRadioDropdownProps<T> extends AbstractSingleFilterDropdownProps<T> {
  optionList: Option<T>[];
}

export function GenericRadioDropdown<T>({
  currentValue,
  optionalDefaultValue,
  onApply,
  optionList = [],
  filterName,
  header,
}: GenericRadioDropdownProps<T>): JSX.Element {
  const [dirtyValue, setDirtyValue] = useState(
    currentValue ?? optionalDefaultValue ?? optionList.find(() => true)?.value,
  );
  const [validationError, setValidationError] = useState<ValidationErrorEnum>();
  const handleChange = useCallback(
    (newValue) => {
      setDirtyValue(newValue);
      setValidationError(undefined);
    },
    [setDirtyValue, setValidationError],
  );

  const handleApply = useCallback(() => {
    if (dirtyValue != null) {
      onApply(dirtyValue);
    } else {
      setValidationError(ValidationErrorEnum.SELECT_ONE_OPTION);
    }
  }, [onApply, dirtyValue]);

  return (
    <GenericFilterDropdown ariaLabel={`${filterName} dropdown`} onApply={handleApply}>
      {header != null && <GenericFilterHeader>{header}</GenericFilterHeader>}
      <GenericFilterBody>
        <FilterRadioContainer>
          {optionList.map((option) => {
            return (
              <FilterItemRadio
                isChecked={option.value === dirtyValue}
                id={`${filterName}-dropdown-${option.value}`}
                key={option.label}
                name={option.label}
                onChange={() => handleChange(option.value)}
                value={`${option.value}`}
                labelContent={
                  <Stack direction="row" spacing="x1">
                    {option.content != null && <>{option.content}</>}
                    {option.label}
                  </Stack>
                }
                isInvalid={!!validationError}
              />
            );
          })}
          {!!validationError && <Text textColor="text_error">{validationError}</Text>}
        </FilterRadioContainer>
      </GenericFilterBody>
      <GenericFilterFooter />
    </GenericFilterDropdown>
  );
}
